import { useQuery, useQueryClient, useQueries } from "@tanstack/react-query";
import { useEffect, useRef } from "react";
import { useSnackbar } from "notistack";

import { useLoadingScreen } from '../../Store/LoadingManager';

import QueryBuilderHelper from "../../Utilities/Helpers/QueryBuilderHelper";
import DropdownOptionsHelper from "../../Utilities/Helpers/DropdownOptionsHelper";

import { CachingEnum } from '../../Utilities/Enums/CachingEnum';

import { GetBillings, GetBillingById, CreateBillingService, EditBillingService, SettleBillingService, DeleteBillingService } from "../../Api/Service/Billings/billingsService";
import { StripeWebhookService, StripeSetupIntentService, StripeMakePaymentService } from "../../Api/Service/Billings/stripeService";

import { ErrorMessage, SuccessMessage } from "../../Constants/Messages";
import { BillingStatus, BillingStatusEnum } from "../../Utilities/Enums/StatusEnums";

export function useBillingHook(q_queryKey: any, params: any, runInitialFetch: boolean = true) {
    const queryClient = useQueryClient();
    const { enqueueSnackbar } = useSnackbar();
    const DropdownList = [CachingEnum.COUNTRY_LIST, CachingEnum.CHECKOUT_STEP_LIST, CachingEnum.PAYMENT_TYPE_LIST, CachingEnum.BANK_LIST, CachingEnum.EWALLET_LIST, CachingEnum.LICENSE_TYPE, CachingEnum.VALIDITY_LIST];
    let { loading, setLoading } = useLoadingScreen();

    // Initial fetch
    const { data, fetchStatus } = useQuery({
        queryKey: q_queryKey,
        queryFn: () => getBillingsList(params),
        enabled: runInitialFetch,
    });

    const { data: DropdownData, pending: dropdownFetchStatus } = useQueries({
        queries: DropdownList.map((item) => {
            return {
                queryKey: [item],
                queryFn: () => DropdownOptionsHelper.getLists(item),
            }
        }),
        combine: (results) => {
            const dictionary = Object.assign({}, ...results.map((x, index) => ({ [DropdownList[index]]: x.data })));
            return {
                data: dictionary,
                pending: results.some((result) => result.isPending),
            }
        },
    })

    const getBillingsList = async (params: any) => {
        let _data;
        let _legends;
        const responseData = await GetBillings(QueryBuilderHelper.BuildQueryString(params));
        if (responseData?.isSuccess) {
            _data = responseData?.data
        } else {
            enqueueSnackbar(responseData?.message ?? (responseData?.data?.message ?? ErrorMessage.PROBLEM_FETCHING_DATA), { variant: 'error' })
        }

        _legends = finalizeLegend(_data)
        return { ..._legends, data: _data?.data}
    }

    const finalizeLegend = (_data: any) => {
        const countDict = _data?.data?.metaData?.statusCount
        const totalCount = countDict?.find((x: any | null) => x?.status == '-1')?.count ?? 0
        const legends = ([
            {
                ...BillingStatus[BillingStatusEnum.ALL],
                count: totalCount
            },
            {
                ...BillingStatus[BillingStatusEnum.PAID],
                count: countDict?.find((x: any | null) => x?.status == BillingStatusEnum.PAID)?.count ?? 0
            },
            {
                ...BillingStatus[BillingStatusEnum.UNPAID],
                count: countDict?.find((x: any | null) => x?.status == BillingStatusEnum.UNPAID)?.count ?? 0
            },
        ])
        return { legends: legends, totalCount: totalCount }
    }

    const getBillingById = async (id: string | number) => {
        setLoading(true);

        const responseData = await GetBillingById(id);

        setLoading(false);
        
        return responseData?.data ?? null;
    }

    const stripeSetupIntent = async () => {
        setLoading(true);
        const responseData = await StripeSetupIntentService();

        if (responseData?.isSuccess) {
            queryClient.invalidateQueries(q_queryKey);
        } else {
            enqueueSnackbar(responseData?.message ?? (responseData?.data?.message ?? ErrorMessage.PROBLEM_FETCHING_DATA), { variant: 'error' })
        }

        setLoading(false);

        return responseData;
    }

    const stripemakePayment = async (params: any) => {
        setLoading(true);
        const responseData = await StripeMakePaymentService(params);

        if (responseData?.isSuccess) {
            // queryClient.invalidateQueries(q_queryKey);
            // enqueueSnackbar(responseData?.data?.message ?? "Invoice " + SuccessMessage.CREATED_SUCCESSFULLY, { variant: 'success' })
        } else {
            enqueueSnackbar(responseData?.message ?? (responseData?.data?.message ?? ErrorMessage.PROBLEM_CREATING_DATA), { variant: 'error' })
        }

        // setLoading(false);

        return responseData;
    }

    const createBilling = async (params: any) => {
        setLoading(true);
        const responseData = await CreateBillingService(params);

        if (responseData?.isSuccess) {
            queryClient.invalidateQueries(q_queryKey);
            enqueueSnackbar(responseData?.data?.message ?? "Invoice " + SuccessMessage.CREATED_SUCCESSFULLY, { variant: 'success' })
        } else {
            enqueueSnackbar(responseData?.message ?? (responseData?.data?.message ?? ErrorMessage.PROBLEM_CREATING_DATA), { variant: 'error' })
        }

        setLoading(false);

        return responseData;
    }

    const editBillingById = async (id: any, params: any) => {
        setLoading(true);

        const responseData = await EditBillingService(id, params);

        if (responseData?.isSuccess) {
            queryClient.invalidateQueries(q_queryKey);
            enqueueSnackbar(responseData?.data?.message ?? "Invoice " + SuccessMessage.UPDATED_SUCCESSFULLY, { variant: 'success' })
        } else {
            enqueueSnackbar(responseData?.message ?? (responseData?.data?.message ?? ErrorMessage.PROBLEM_UPDATING_DATA), { variant: 'error' })
        }

        setLoading(false);

        return responseData;
    }

    const settleBillingById = async (id: any, params: any) => {
        setLoading(true);

        const responseData = await SettleBillingService(id, params);

        if (responseData?.isSuccess) {
            queryClient.invalidateQueries(q_queryKey);
            enqueueSnackbar(responseData?.data?.message ?? "Invoice " + SuccessMessage.UPDATED_SUCCESSFULLY, { variant: 'success' })
        } else {
            enqueueSnackbar(responseData?.message ?? (responseData?.data?.message ?? ErrorMessage.PROBLEM_UPDATING_DATA), { variant: 'error' })
        }

        setLoading(false);

        return responseData;
    }

    const deleteBillingById = async (id: any, remarks?: any) => {
        setLoading(true);

        const responseData = await DeleteBillingService(id, JSON.stringify({remarks: remarks ?? ""}));

        if (responseData?.isSuccess) {
            queryClient.invalidateQueries(q_queryKey);
            enqueueSnackbar(responseData?.data?.message ?? "Billing " + SuccessMessage.DELETED_SUCCESSFULLY, { variant: 'success' })
        } else {
            enqueueSnackbar(responseData?.message ?? (responseData?.data?.message ?? ErrorMessage.PROBLEM_DELETING_DATA), { variant: 'error' })
        }

        setLoading(false);

        return responseData;
    }

    return { data, DropdownData, fetchStatus, dropdownFetchStatus, getBillingById, createBilling, editBillingById, settleBillingById, deleteBillingById, stripeSetupIntent, stripemakePayment }
}